import { useState, useEffect } from 'react';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';
import ImageSourceModal from '../../../components/ui/ImageSourceModal';
import { useImageCapture } from '../../../hooks/useImageCapture.jsx';
import { useThings } from '../../../hooks/useThings.js';

const ThingForm = ({ 
  editingThing = null, 
  onSave = () => {}, 
  onCancel = () => {},
  mode = 'create', // 'create', 'edit'
  addThing,
  updateThing,
  deleteThing
}) => {
  
  const { 
    things: thingsData, 
    getUniqueTypeThings
  } = useThings();
  
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    description: '',
    type_thing: '',
    available: true,
    images: [],
  });

  const [typeThingHistory, setTypeThingHistory] = useState([]);
  const [showTypeThingInput, setShowTypeThingInput] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Hook para captura de imágenes
  const {
    isSourceModalOpen,
    openImageSourceModal,
    closeImageSourceModal,
    handleImageSourceSelect,
    createHiddenInputs
  } = useImageCapture();

  // Cargar tipos únicos desde la base de datos
  useEffect(() => {
    if (getUniqueTypeThings) {
      const uniqueTypes = getUniqueTypeThings();
      setTypeThingHistory(uniqueTypes);
    }
  }, [thingsData, getUniqueTypeThings]);

  // Cargar datos del producto en modo edición
  useEffect(() => {
    if (editingThing && mode === 'edit') {
      setFormData({
        title: editingThing.title_thing || '',
        price: editingThing.price_thing || '',
        description: editingThing.description_thing || '',
        type_thing: editingThing.type_thing || '',
        available: editingThing.available_thing === true,
        images: editingThing.image_urls || [],
      });
    }
  }, [editingThing, mode]);

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Limpiar mensajes previos
    setError(null);
    setSuccess(null);

    // Validar que al menos una imagen esté presente
    const processedImages = formData.images.filter(img => img);
    if (processedImages.length === 0) {
      setError('Debe agregar al menos una imagen del producto');
      return;
    }

    setIsSubmitting(true);
    try {
      const processedFormData = {
        ...formData,
        images: processedImages,
        available: formData.available
      };

      if (mode === 'edit' && editingThing) {
        await updateThing(editingThing.thing_id, {
          ...processedFormData,
          image_urls: processedImages
        });
        setSuccess('Producto actualizado exitosamente');
        
        // Cerrar modal y recargar página después de un breve delay
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
          window.location.reload();
        }, 1500);
      } else {
        await addThing({
          ...processedFormData,
          image_urls: processedImages
        });
        
        // El historial se actualizará automáticamente al recargar los datos
        
        setSuccess('Producto creado exitosamente');
        
        // Cerrar modal después de un breve delay para mostrar el mensaje
        setTimeout(() => {
          onSave();
          // Recargar catálogo principal si está disponible
          if (window.refreshBookCatalog) {
            window.refreshBookCatalog();
          }
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving thing:', error);
      setError(error.message || 'Error al guardar el producto. Por favor, intente nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manejar eliminación
  const handleDelete = async () => {
    if (!editingThing || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await deleteThing(editingThing.thing_id);
      onSave();
    } catch (error) {
      console.error('Error deleting thing:', error);
    } finally {
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  const [imageIndex, setImageIndex] = useState(0);

  // Función para convertir archivo a base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Los tipos se obtienen directamente de la base de datos, no necesitamos funciones de gestión manual

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      const selectedFiles = Array.from(files).slice(0, 5);
      
      // Convertir archivos a Data URLs (Base64)
      const imageUrls = await Promise.all(
        selectedFiles.map(file => convertToBase64(file))
      );
      
      setFormData((prev) => ({ ...prev, images: imageUrls }));
      setImageIndex(0);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Función para remover imagen
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = null;
    // Si se elimina la portada (índice 0), también eliminar del marco 1
    if (index === 0) {
      updatedImages[1] = null;
    }
    // Si se elimina del marco 1, también eliminar la portada
    if (index === 1) {
      updatedImages[0] = null;
    }
    setFormData((prev) => ({ ...prev, images: updatedImages }));
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="bg-white border border-slate-200 rounded-xl p-6 space-y-6 max-w-5xl mx-auto"
      >
      {/* Mensajes de error y éxito */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <div className="text-green-600 text-sm">{success}</div>
          </div>
        </div>
      )}

      {/* IMAGEN + CAMPOS LATERALES */}
      <div className="flex flex-wrap gap-6">
        {/* Marcos opcionales */}
        <div className="flex flex-wrap gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="relative w-[100px] h-[120px] border border-dashed border-slate-300 rounded-md bg-slate-50 overflow-hidden flex items-center justify-center"
            >
              <div
                onClick={() => openImageSourceModal((base64Url) => {
                  const updatedImages = [...formData.images];
                  updatedImages[i] = base64Url;
                  // Si es el marco 1 (Portada), también actualizar el índice 0
                  if (i === 1) updatedImages[0] = base64Url;
                  setFormData((prev) => ({ ...prev, images: updatedImages }));
                })}
                className="absolute inset-0 cursor-pointer"
              />
              {(i === 1 ? formData.images[0] : formData.images[i]) ? (
                <>
                  <img
                    src={i === 1 ? formData.images[0] : formData.images[i]}
                    alt={`extra-${i}`}
                    className="w-full h-full object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold hover:bg-red-600 transition-colors"
                  >
                    ×
                  </button>
                </>
              ) : (
                <>
                  <span className="text-slate-400 text-xl">+</span>
                  <span className="absolute bottom-1 text-[10px] text-slate-400">
                    {i === 1 ? 'Portada' : 'Opcional'}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Imagen de Portada */}
        <div className="relative w-[250px] h-[300px] border border-slate-300 rounded-md bg-slate-50 overflow-hidden flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div
            onClick={() => openImageSourceModal((base64Url) => {
              const updatedImages = [...formData.images];
              updatedImages[0] = base64Url;
              updatedImages[1] = base64Url; // reflejo en primer marco opcional (Portada)
              setFormData((prev) => ({ ...prev, images: updatedImages }));
            })}
            className="absolute inset-0 cursor-pointer"
          />
          {formData.images[0] ? (
            <>
              <div className="relative w-[120px] h-[155px] flex items-center justify-center">
                <img
                  src={formData.images[0]}
                  alt="Imagen de Portada"
                  className="w-full h-full object-cover rounded"
                />
              </div>
              <button
                type="button"
                onClick={() => removeImage(0)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-600 transition-colors"
              >
                ×
              </button>
            </>
          ) : (
            <span className="text-slate-400 text-sm pointer-events-none text-center px-4">
              Imagen de Portada
            </span>
          )}
        </div>

        {/* Inputs de Título, Precio y Descripción */}
        <div className="flex-1 grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Título
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Ingrese título del producto"
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Precio
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              placeholder="Ingrese precio"
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tipo de Producto
            </label>
            
            {/* Mostrar select si hay historial, sino input directo */}
            {typeThingHistory.length > 0 && !showTypeThingInput ? (
              <div className="space-y-2">
                <select
                  name="type_thing"
                  value={formData.type_thing}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
                >
                  <option value="">Seleccionar tipo existente...</option>
                  {typeThingHistory.map((type, index) => (
                    <option key={index} value={type}>{type}</option>
                  ))}
                </select>
                
                {/* Botón para agregar nuevo tipo */}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowTypeThingInput(true)}
                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-colors"
                  >
                    + Agregar nuevo tipo
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  name="type_thing"
                  value={formData.type_thing}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Escolar, Oficina, Artístico, etc."
                  className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
                />
                
                {typeThingHistory.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowTypeThingInput(false)}
                    className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded hover:bg-gray-200 transition-colors"
                  >
                    ← Volver a tipos guardados
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DESCRIPCIÓN */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Descripción
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          rows={4}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800 resize-none"
          placeholder="Descripción del producto..."
        />
      </div>

      {/* DISPONIBILIDAD */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Disponibilidad
        </label>
        <select
          name="available"
          value={formData.available.toString()}
          onChange={(e) => setFormData(prev => ({ ...prev, available: e.target.value === 'true' }))}
          className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800"
        >
          <option value="true">Disponible</option>
          <option value="false">No disponible</option>
        </select>
      </div>

      {/* BOTONES */}
      <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed text-center"
          >
            {isSubmitting ? 'Guardando...' : (mode === 'edit' ? 'Actualizar producto' : 'Crear producto')}
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            className="bg-slate-200 text-slate-700 px-5 py-2.5 rounded-lg font-medium hover:bg-slate-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 text-center"
          >
            Cancelar
          </button>
        </div>

        {mode === 'edit' && editingThing && (
          <div className="w-full sm:w-auto">
            {showDeleteConfirm ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 text-center"
                >
                  {isSubmitting ? 'Eliminando...' : 'Confirmar'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium hover:bg-slate-300 transition-all duration-200 text-center"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full sm:w-auto bg-orange-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-center"
              >
                Eliminar producto
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="¿Eliminar producto?"
        message="Esta acción no se puede deshacer."
        itemName={editingThing?.title_thing}
        isLoading={isSubmitting}
      />
      </form>

      {/* Modal de selección de fuente de imagen */}
      <ImageSourceModal
        isOpen={isSourceModalOpen}
        onClose={closeImageSourceModal}
        onSelectSource={handleImageSourceSelect}
      />

      {/* Inputs ocultos para captura de imágenes */}
      {createHiddenInputs()}
    </div>
  );
};

export default ThingForm;

